import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, BookOpen, ExternalLink, LockKeyhole, Search } from "lucide-react";
import styles from "@/components/library/LibrarySourcesVisual.module.css";
import { Button } from "@/components/ax/Button";
import { EmptyAxis } from "@/components/ax/EmptyAxis";
import { useFirstView } from "@/hooks/useFirstView";
import { useI18n } from "@/i18n";
import { getSourceCatalog } from "@/lib/sourceStore";
import { logLibraryEvent } from "@/lib/libraryAnalytics";

export const SOURCES_COPY = {
  id: {
    eyebrow: "Pustaka / Sumber terbuka",
    title: "Sumber",
    lede: "Tautan ke teks berlisensi. Bukan salinan AXORIS.",
    searchLabel: "Cari judul, penulis, atau subjek",
    searchPlaceholder: "Cari judul, penulis, subjek, ISBN, atau DOI",
    searchButton: "Cari",
    resetFilters: "Reset saringan",
    filterAvailability: "Ketersediaan",
    availabilityAll: "Semua",
    availabilityHosted: "Tersedia di AXORIS",
    availabilityLinkOnly: "Tautan sumber",
    filterLanguage: "Bahasa",
    languageAll: "Semua bahasa",
    languageId: "Bahasa Indonesia",
    languageEn: "English",
    filterSubject: "Subjek",
    subjectAll: "Semua subjek",
    badgeHosted: "Tersedia di AXORIS",
    badgeLinkOnly: "Tautan sumber",
    readAtSource: "Baca di sumber",
    openInLibrary: "Buka di Pustaka",
    empty: "Belum ada sumber yang dibuka.",
    filterEmpty: "Tidak ada sumber pada saringan ini.",
    failLoad: "Katalog sumber belum dapat dimuat. Coba lagi.",
    loadMore: "Muat lebih banyak",
    loadingMore: "Memuat...",
    countLabel: "{n} sumber",
  },
  en: {
    eyebrow: "Library / Open sources",
    title: "Sources",
    lede: "Links to licensed texts. Not AXORIS copies.",
    searchLabel: "Search title, author, or subject",
    searchPlaceholder: "Search title, author, subject, ISBN, or DOI",
    searchButton: "Search",
    resetFilters: "Reset filters",
    filterAvailability: "Availability",
    availabilityAll: "All",
    availabilityHosted: "Available in AXORIS",
    availabilityLinkOnly: "Source link",
    filterLanguage: "Language",
    languageAll: "All languages",
    languageId: "Bahasa Indonesia",
    languageEn: "English",
    filterSubject: "Subject",
    subjectAll: "All subjects",
    badgeHosted: "Available in AXORIS",
    badgeLinkOnly: "Source link",
    readAtSource: "Read at source",
    openInLibrary: "Open in Library",
    empty: "No sources are available yet.",
    filterEmpty: "No sources match this filter.",
    failLoad: "The source catalog could not be loaded. Try again.",
    loadMore: "Load more",
    loadingMore: "Loading...",
    countLabel: "Sources: {n}",
  },
};

const SUBJECT_OPTIONS = [
  "Strategy",
  "Leadership",
  "Management",
  "Finance",
  "Economics",
  "Decision Making",
  "Governance",
  "Innovation",
];

const Skeleton = () => (
  <div aria-busy="true" data-testid="sources-skeleton" className={styles.skeleton}>
    {[0, 1, 2, 3].map((i) => (
      <div key={i} className={styles.skeletonCard}>
        <div className={styles.skeletonLine} style={{ width: "40%" }} />
        <div className={styles.skeletonLine} style={{ width: "85%" }} />
        <div className={styles.skeletonLine} style={{ width: "60%" }} />
      </div>
    ))}
  </div>
);

export default function LibrarySources() {
  const { t, lang } = useI18n();
  const locale = lang === "en" ? "en" : "id";
  const copy = SOURCES_COPY[locale];
  const first = useFirstView("library-sources");

  const [state, setState] = useState("loading");
  const [sources, setSources] = useState([]);
  const [nextCursor, setNextCursor] = useState(null);
  const [locked, setLocked] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  const [searchInput, setSearchInput] = useState("");
  const [appliedQuery, setAppliedQuery] = useState("");
  const [availability, setAvailability] = useState("all");
  const [language, setLanguage] = useState("");
  const [subject, setSubject] = useState("");

  const hasActiveFilter =
    appliedQuery.trim().length > 0 ||
    availability !== "all" ||
    language !== "" ||
    subject !== "";

  const load = useCallback(async () => {
    setState("loading");
    const res = await getSourceCatalog({
      q: appliedQuery,
      availability,
      language: language || undefined,
      subject: subject || undefined,
      limit: 20,
    });
    if (res.error) {
      setState("error");
      return;
    }
    setLocked(Boolean(res.locked));
    setSources(res.sources || []);
    setNextCursor(res.nextCursor ?? null);
    setState("ready");
  }, [appliedQuery, availability, language, subject]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setAppliedQuery(searchInput.trim());
  };

  const handleResetFilters = () => {
    setSearchInput("");
    setAppliedQuery("");
    setAvailability("all");
    setLanguage("");
    setSubject("");
  };

  const handleLoadMore = async () => {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    const res = await getSourceCatalog({
      q: appliedQuery,
      availability,
      language: language || undefined,
      subject: subject || undefined,
      cursor: nextCursor,
      limit: 20,
    });
    setLoadingMore(false);
    if (res.error) return;
    setSources((prev) => {
      const seen = new Set(prev.map((s) => s.id));
      const appended = (res.sources || []).filter((s) => !seen.has(s.id));
      return [...prev, ...appended];
    });
    setNextCursor(res.nextCursor ?? null);
  };

  return (
    <div data-testid="sources-page" className={styles.page}>
      <Link to="/library" data-testid="sources-back-link" className={styles.back}>
        <ArrowLeft size={16} strokeWidth={1.5} aria-hidden="true" />
        {t("book.back")}
      </Link>

      <header className={styles.heading}>
        <div>
          <p className={styles.eyebrow}>{copy.eyebrow}</p>
          <h1 className={styles.title} data-testid="sources-title">{copy.title}</h1>
          <p className={styles.lede} data-testid="sources-lede">{copy.lede}</p>
        </div>
      </header>

      {!locked && (
        <form
          onSubmit={handleSearchSubmit}
          data-testid="sources-controls"
          className={styles.controls}
        >
          <div className={styles.searchRow}>
            <input
              type="search"
              aria-label={copy.searchLabel}
              placeholder={copy.searchPlaceholder}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              data-testid="sources-search-input"
              className={styles.searchInput}
            />
            <Button type="submit" variant="secondary" data-testid="sources-search-button">
              <Search size={16} strokeWidth={1.5} aria-hidden="true" />
              {copy.searchButton}
            </Button>
            {hasActiveFilter && (
              <Button
                type="button"
                variant="ghost"
                onClick={handleResetFilters}
                data-testid="sources-reset-button"
              >
                {copy.resetFilters}
              </Button>
            )}
          </div>

          <div className={styles.filterRow}>
            <label className={styles.filterGroup}>
              <span className={styles.filterLabel}>{copy.filterAvailability}</span>
              <select
                value={availability}
                onChange={(e) => setAvailability(e.target.value)}
                data-testid="sources-filter-availability"
                className={styles.select}
              >
                <option value="all">{copy.availabilityAll}</option>
                <option value="hosted">{copy.availabilityHosted}</option>
                <option value="link_only">{copy.availabilityLinkOnly}</option>
              </select>
            </label>

            <label className={styles.filterGroup}>
              <span className={styles.filterLabel}>{copy.filterLanguage}</span>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                data-testid="sources-filter-language"
                className={styles.select}
              >
                <option value="">{copy.languageAll}</option>
                <option value="id">{copy.languageId}</option>
                <option value="en">{copy.languageEn}</option>
              </select>
            </label>

            <label className={styles.filterGroup}>
              <span className={styles.filterLabel}>{copy.filterSubject}</span>
              <select
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                data-testid="sources-filter-subject"
                className={styles.select}
              >
                <option value="">{copy.subjectAll}</option>
                {SUBJECT_OPTIONS.map((subj) => (
                  <option key={subj} value={subj}>
                    {subj}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </form>
      )}

      {state === "loading" && <Skeleton />}

      {state === "error" && (
        <div role="alert" data-testid="sources-error" className={styles.error}>
          <p className="m-0 text-base text-ax-text-2">{copy.failLoad}</p>
          <Button onClick={load} data-testid="sources-retry-button" className="mt-4">
            {t("retry")}
          </Button>
        </div>
      )}

      {state === "ready" && locked && (
        <div data-testid="sources-locked" className={styles.locked}>
          <LockKeyhole size={20} strokeWidth={1.5} aria-hidden="true" />
          <p>{t("lib.locked")}</p>
        </div>
      )}

      {state === "ready" && !locked && sources.length === 0 && !hasActiveFilter && (
        <section data-testid="sources-empty" className={styles.empty}>
          <EmptyAxis play={first} />
          <p className="m-0 mt-4 text-base text-ax-text">{copy.empty}</p>
        </section>
      )}

      {state === "ready" && !locked && sources.length === 0 && hasActiveFilter && (
        <section data-testid="sources-filter-empty" className={styles.empty}>
          <p className="m-0 text-base text-ax-text">{copy.filterEmpty}</p>
        </section>
      )}

      {state === "ready" && !locked && sources.length > 0 && (
        <section aria-label={copy.title}>
          <div className={styles.catalogHeader}>
            <span className={styles.count} data-testid="sources-count">
              {copy.countLabel.replace("{n}", String(sources.length))}
            </span>
          </div>

          <ul data-testid="sources-list" className={styles.cards}>
            {sources.map((item) => {
              const isHosted =
                item.availabilityMode === "hosted" ||
                (Boolean(item.hostedBookId) &&
                  (item.rightsClass === "HOST" ||
                    item.rightsClass === "HOST_ATTRIBUTION"));
              return (
                <li key={item.id}>
                  <article
                    data-testid={`source-card-${item.id}`}
                    className={styles.card}
                  >
                    <div className={styles.cardTop}>
                      <div className={styles.badgeRow}>
                        <span
                          data-testid={`source-badge-${item.id}`}
                          className={
                            isHosted ? styles.badgeHosted : styles.badgeLinkOnly
                          }
                        >
                          {isHosted ? copy.badgeHosted : copy.badgeLinkOnly}
                        </span>
                        <span
                          data-testid={`source-license-${item.id}`}
                          className={styles.licenseBadge}
                        >
                          {item.licenseCode}
                        </span>
                      </div>

                      <h2
                        data-testid={`source-title-${item.id}`}
                        className={styles.cardTitle}
                      >
                        {item.title}
                      </h2>

                      <p
                        data-testid={`source-authors-${item.id}`}
                        className={styles.authors}
                      >
                        {Array.isArray(item.authors)
                          ? item.authors.join(", ")
                          : ""}
                      </p>

                      <div className={styles.metaRow}>
                        {item.year != null && (
                          <span data-testid={`source-year-${item.id}`}>
                            {item.year}
                          </span>
                        )}
                        {item.language && (
                          <span data-testid={`source-language-${item.id}`}>
                            {item.language.toUpperCase()}
                          </span>
                        )}
                      </div>

                      {Array.isArray(item.subjects) && item.subjects.length > 0 && (
                        <ul
                          data-testid={`source-subjects-${item.id}`}
                          className={styles.subjects}
                        >
                          {item.subjects.map((subj) => (
                            <li key={subj} className={styles.subjectTag}>
                              {subj}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    <div className={styles.actions}>
                      <a
                        href={item.sourceUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`source-external-link-${item.id}`}
                        className={styles.externalLink}
                        onClick={() => {
                          void logLibraryEvent("external_source_open", {
                            availabilityMode: item.availabilityMode,
                            sourceName: item.sourceName,
                          });
                        }}
                      >
                        {copy.readAtSource}
                        <ExternalLink size={14} strokeWidth={1.5} aria-hidden="true" />
                      </a>

                      {isHosted && item.hostedBookId && (
                        <Link
                          to={`/library/${item.hostedBookId}`}
                          data-testid={`source-hosted-link-${item.id}`}
                          className={styles.hostedLink}
                        >
                          <BookOpen size={14} strokeWidth={1.5} aria-hidden="true" />
                          {copy.openInLibrary}
                        </Link>
                      )}
                    </div>
                  </article>
                </li>
              );
            })}
          </ul>

          {nextCursor && (
            <div className={styles.loadMoreWrap}>
              <Button
                type="button"
                variant="secondary"
                disabled={loadingMore}
                onClick={handleLoadMore}
                data-testid="sources-load-more"
              >
                {loadingMore ? copy.loadingMore : copy.loadMore}
              </Button>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
